// The buzz of a telephone (ui-map-interface.md §13 "Buzz", R1612, R1615;
// mockup round 077, card 11 "A BUZZ column in the ALERTS card").
//
// While its tab is visible, the game buzzes on five events: a siege
// warning, the hold under fire, a hit on an own fleet, a fleet lost, and a
// build or research done. Four of them are the push classes of
// `[events.push]`, and a buzz row takes the event ids of its class from
// the event table (balancegen, `BUZZ_EVENTS`), so the BUZZ and the PUSH
// column of one row hear one list. The fifth, the hit, has no push class:
// the sim's hit events raise it. A hidden or closed tab is the work of the
// push notifications, and the telephone vibrates for a push by itself.
//
// Two limits keep the motor quiet (R1615):
//
// - At most one buzz in `BUZZ_GAP_MS`. When two events meet inside the
//   gap, the longer pattern wins: a longer one replaces the running buzz
//   and starts the gap again, and a shorter or equal one is dropped.
// - A hit buzzes once in `BUZZ_HIT_GAP_MS` for each fleet.
//
// A calm event (`BUZZ_CALM_EVENTS`, R3085) names no threat: an attack pledge
// of the own faction and the siege windows of the day. It buzzes `BUZZ_CALM_PATTERN` under the
// switch of its row, so it never plays the three pulses of a siege.
//
// Every BUZZ switch is on until the player turns it off, so the settings
// blob stores the switches that are OFF (`buzzOff`, R1613). The support is
// read off the API and the primary pointer, never off the user agent: a
// telephone with `navigator.vibrate` buzzes, a telephone without it (an
// iPhone) cannot, and a desktop has no motor (`buzzForm`). The iOS store
// app is the one exception: its app shell plays the pattern through Core
// Haptics on the `buzz` op of the bridge (mobile-apps.md §8, R2927). The
// gap, the hit gap and the visible-tab rule hold there too.

import {
  BUZZ_CALM_EVENTS, BUZZ_CALM_PATTERN, BUZZ_EVENTS, BUZZ_GAP_MS, BUZZ_HIT_GAP_MS, type BuzzEventId,
} from '../gen/presentation';
import { EventKind, type Event as WireEvent } from '../gen/proto/aquaphobia';
import { quickCode } from './quickchat';
import { bridgeHas, bridgeSend, pageWindow, type BridgeWindow } from '../platform/bridge';
import { coarsePointer } from './pointer';

/** What the device can do: a motor, a telephone with none, or a desktop. */
export type BuzzForm = 'motor' | 'none' | 'desk';

/** What the buzz reads of the browser, so a test passes plain values. */
export interface BuzzEnv {
  /** `navigator.vibrate`, bound, or null where the browser has none. */
  vibrate: ((pattern: number[]) => boolean) | null;
  /** The primary pointer is a finger: a telephone or a tablet. */
  coarse: boolean;
  /** The tab is on the screen. */
  visible(): boolean;
  /** A clock in ms. */
  now(): number;
}

/** The browser's own answers. Every read is guarded: a page with no
 *  window, or a browser that throws on a query, has no motor. The iOS
 *  app shell's `buzz` op stands in for `navigator.vibrate`. */
export function browserBuzzEnv(
  bridge: BridgeWindow | null = pageWindow(),
): BuzzEnv {
  let vibrate: BuzzEnv['vibrate'] = null;
  let coarse = false;
  try {
    const nav = typeof navigator === 'undefined' ? null : navigator;
    if (bridgeHas('buzz', bridge)) vibrate = (pattern) => bridgeSend('buzz', { pattern }, bridge);
    else if (nav && typeof nav.vibrate === 'function') vibrate = (p) => nav.vibrate(p);
    coarse = typeof matchMedia === 'function' ? coarsePointer() : (nav?.maxTouchPoints ?? 0) > 0;
  } catch { /* no motor */ }
  return {
    vibrate,
    coarse,
    visible: () => typeof document === 'undefined' || document.visibilityState === 'visible',
    now: () => performance.now(),
  };
}

/** The form of a device (R1615): what the ALERTS card shows and whether
 *  the game buzzes at all. */
export function buzzForm(env: Pick<BuzzEnv, 'vibrate' | 'coarse'>): BuzzForm {
  if (!env.coarse) return 'desk';
  return env.vibrate ? 'motor' : 'none';
}

/** The whole length of a pattern, on and off, in ms. */
export function patternMs(pattern: readonly number[]): number {
  return pattern.reduce((a, b) => a + b, 0);
}

/** One buzz row by its id. */
export function buzzRow(id: BuzzEventId): (typeof BUZZ_EVENTS)[number] {
  return BUZZ_EVENTS.find((e) => e.id === id) ?? BUZZ_EVENTS[0];
}

/** The buzz row of each event id of the event table. */
const BY_EVENT: ReadonlyMap<string, BuzzEventId> = new Map(
  BUZZ_EVENTS.flatMap((row) => row.events.map((ev): [string, BuzzEventId] => [ev, row.id])),
);

/** The buzz event a notice's event id raises, or null. */
export function buzzOfEvent(id: string): BuzzEventId | null {
  return BY_EVENT.get(id) ?? null;
}

/** The buzz row of a hit on an own fleet. */
const HIT: BuzzEventId = 'hit';

/** How many fleets the hit ledger holds before it drops the old ones. */
const HIT_LEDGER_MAX = 64;

export class Buzzer {
  private readonly form: BuzzForm;
  private lastAt = -Infinity;
  private lastLen = 0;
  private readonly hitAt = new Map<number, number>();

  /** `on` answers whether the player left a BUZZ switch on. */
  constructor(private readonly env: BuzzEnv, private readonly on: (id: BuzzEventId) => boolean) {
    this.form = buzzForm(env);
  }

  /**
   * One event. It answers whether the motor ran, for the tests. A switch
   * that is off, a hidden tab, a device with no motor and the gap all
   * answer false. `pattern` stands in for the pattern of the row: the
   * calm pattern of a calm event (R3085).
   */
  buzz(id: BuzzEventId, pattern: readonly number[] = buzzRow(id).pattern): boolean {
    if (this.form !== 'motor' || !this.env.vibrate || !this.on(id) || !this.env.visible()) return false;
    const len = patternMs(pattern);
    const now = this.env.now();
    if (now - this.lastAt < BUZZ_GAP_MS && len <= this.lastLen) return false;
    this.env.vibrate([...pattern]);
    this.lastAt = now;
    this.lastLen = len;
    return true;
  }

  /** A hit on one own fleet: once in `BUZZ_HIT_GAP_MS` for each fleet. */
  hit(fleetId: number): boolean {
    const now = this.env.now();
    const last = this.hitAt.get(fleetId);
    if (last !== undefined && now - last < BUZZ_HIT_GAP_MS) return false;
    if (!this.buzz(HIT)) return false;
    if (this.hitAt.size >= HIT_LEDGER_MAX) {
      for (const [id, at] of this.hitAt) if (now - at >= BUZZ_HIT_GAP_MS) this.hitAt.delete(id);
    }
    this.hitAt.set(fleetId, now);
    return true;
  }

  /** A notice off the wire: its event id may raise a buzz. */
  onNotice(eventId: string): void {
    const id = buzzOfEvent(eventId);
    if (id === null) return;
    if (BUZZ_CALM_EVENTS.includes(eventId)) this.buzz(id, BUZZ_CALM_PATTERN);
    else this.buzz(id);
  }

  /**
   * A chat line off the wire: the H-hour quick line of a squad mate on the
   * SQUAD channel buzzes the siege row, so RALLY reaches the telephone of
   * every mate whose tab is visible. A line of the reader and a line of
   * the backlog buzz nothing.
   */
  onChat(line: { history: boolean; quick: number; fromId: number }, squad: boolean, selfId: number): boolean {
    if (line.history || !squad || line.fromId === 0 || line.fromId === selfId) return false;
    return quickCode(line.quick) === 'siege' ? this.buzz('siege') : false;
  }

  /** The transient events of one tick: a hit on a fleet of this seat. */
  onEvents(events: readonly WireEvent[], playerId: number): void {
    if (this.form !== 'motor' || playerId === 0) return;
    for (const e of events) {
      // A Wrasse repair rides a TORPEDO_HIT with `heal` (R2783): no hit.
      if ((e.kind === EventKind.EVENT_KIND_HIT || e.kind === EventKind.EVENT_KIND_TORPEDO_HIT)
        && !e.heal && e.subject === playerId && e.victim !== 0) {
        this.hit(e.victim);
      }
    }
  }
}
